import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Upload, Trash2, Image as ImageIcon } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const CATEGORIES = ["Residenziale", "Terrazzi", "Balconi", "Hospitality"];

export default function PortfolioManager({ token }) {
  const [items, setItems] = useState([]);
  const [category, setCategory] = useState("Residenziale");
  const [title, setTitle] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/portfolio`);
      setItems(data);
    } catch {
      toast.error("Errore nel caricamento delle foto");
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, []);

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    let ok = 0;
    let failed = 0;
    for (const file of files) {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("category", category);
      fd.append("title", title);
      try {
        await axios.post(`${API}/portfolio`, fd, {
          headers: { ...authHeader.headers, "Content-Type": "multipart/form-data" },
        });
        ok += 1;
      } catch {
        failed += 1;
      }
    }
    if (ok) toast.success(`${ok} foto caricate${failed ? `, ${failed} non riuscite` : ""}`);
    else toast.error("Caricamento non riuscito");
    setTitle("");
    if (fileRef.current) fileRef.current.value = "";
    await load();
    setUploading(false);
  };

  const onDelete = async (id) => {
    try {
      await axios.delete(`${API}/portfolio/${id}`, authHeader);
      setItems((arr) => arr.filter((i) => i.id !== id));
      toast.success("Foto rimossa");
    } catch {
      toast.error("Rimozione non riuscita");
    }
  };

  return (
    <div className="bg-white border border-black/10 mb-10" data-testid="portfolio-manager">
      <div className="px-6 py-4 border-b border-black/10 flex items-center gap-2">
        <ImageIcon strokeWidth={1.2} className="w-4 h-4" />
        <h2 className="text-sm uppercase tracking-[0.2em]">Gestione Portfolio</h2>
      </div>

      <div className="p-6 flex flex-col md:flex-row md:items-end gap-4 border-b border-black/10">
        <div className="flex-1">
          <label className="text-xs uppercase tracking-wide text-muted-foreground">Titolo (opzionale)</label>
          <input
            data-testid="portfolio-title-input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Es. Veranda Fronte Mare"
            className="w-full bg-transparent border-b border-black/20 py-2 text-sm focus:border-black focus:outline-none"
          />
        </div>
        <div>
          <label className="block text-xs uppercase tracking-wide text-muted-foreground mb-1">Categoria</label>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger data-testid="portfolio-category-select" className="w-[180px] rounded-none">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CATEGORIES.map((c) => (
                <SelectItem key={c} value={c} data-testid={`pm-cat-${c}`}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <input ref={fileRef} type="file" accept="image/*" multiple onChange={onUpload} className="hidden" data-testid="portfolio-file-input" />
          <button
            data-testid="portfolio-upload-button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="flex items-center gap-2 bg-black text-white px-6 py-2.5 text-sm uppercase tracking-wide hover:bg-black/80 transition-colors disabled:opacity-50"
          >
            <Upload strokeWidth={1.2} className="w-4 h-4" />
            {uploading ? "Caricamento..." : "Carica Foto"}
          </button>
        </div>
      </div>

      <div className="p-6">
        {items.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nessuna foto caricata. Puoi selezionare più foto insieme; appariranno anche nella galleria del sito.</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {items.map((i) => (
              <div key={i.id} data-testid={`pm-item-${i.id}`} className="group relative border border-black/10">
                <img src={`${BACKEND}${i.image_url}`} alt={i.title} className="w-full h-40 object-cover" />
                <div className="absolute inset-x-0 bottom-0 bg-white/80 backdrop-blur-md px-2 py-1 text-[10px] uppercase tracking-wide">
                  {i.category}{i.title ? ` · ${i.title}` : ""}
                </div>
                <button
                  data-testid={`pm-delete-${i.id}`}
                  onClick={() => onDelete(i.id)}
                  className="absolute top-2 right-2 w-8 h-8 flex items-center justify-center bg-white/90 text-destructive opacity-0 group-hover:opacity-100 transition-opacity"
                  aria-label="Elimina"
                >
                  <Trash2 strokeWidth={1.4} className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
